import { z } from 'zod';

export const WoltUserId = z.string().brand('WoltUserId');
export type WoltUserId = z.infer<typeof WoltUserId>;

export const CompanyId = z.string().brand('CompanyId');
export type CompanyId = z.infer<typeof CompanyId>;

export const CompanyPdrn = z.string().brand('CompanyPdrn');
export type CompanyPdrn = z.infer<typeof CompanyPdrn>;

export const VenueId = z.string().brand('VenueId');
export type VenueId = z.infer<typeof VenueId>;

export const CountryCodeAlpha3 = z.string().brand('CountryCodeAlpha3');
export type CountryCodeAlpha3 = z.infer<typeof CountryCodeAlpha3>;

// https://docs.google.com/document/d/1ftn7ir4cCVVlxaNk3y4WFqGFr_69ywHCIcdFM8Qq9Qo/edit?tab=t.0
// FIXME: Long from DeliveryOS, Integer from LCSP, UUID when fully migrated into Taulu
export const UnifiedCourierId = z.coerce.string().brand('UnifiedCourierId');
export type UnifiedCourierId = z.infer<typeof UnifiedCourierId>;

export const CourierPdrn = z.string().brand('CourierPdrn');
export type CourierPdrn = z.infer<typeof CourierPdrn>;

export const CourierGroupPdrn = z.string().brand('CourierGroupPdrn');
export type CourierGroupPdrn = z.infer<typeof CourierGroupPdrn>;

export const CourierId = z.number().brand('CourierId');
export type CourierId = z.infer<typeof CourierId>;

export const ExternalCourierId = z.string().brand('ExternalCourierId');
export type ExternalCourierId = z.infer<typeof ExternalCourierId>;

export const ExternalTaskId = z.string().brand('ExternalTaskId');
export type ExternalTaskId = z.infer<typeof ExternalTaskId>;

export const PurchaseId = z.string().brand('PurchaseId');
export type PurchaseId = z.infer<typeof PurchaseId>;

export const CourierStatus = z.enum([
  'online',
  'offline',
  'unknown',
  // LCSP courier shift status
  'unspecified',
  'noactiveschedule',
  'hasupcomingschedule',
  'hasnotstartedschedule',
  'onschedule',
]);
export type CourierStatus = z.infer<typeof CourierStatus>;

export const ContractType = z.string().brand('ContractType');
export type ContractType = z.infer<typeof ContractType>;

export const AreaId = z.string().brand('AreaId');
export type AreaId = z.infer<typeof AreaId>;

export const CourierGroupId = z.string().brand('CourierGroupId');
export type CourierGroupId = z.infer<typeof CourierGroupId>;

export const CityConfigId = z.number().brand('CityConfigId');
export type CityConfigId = z.infer<typeof CityConfigId>;

export const ConfigurationId = z.string().brand('ConfigurationId');
export type ConfigurationId = z.infer<typeof ConfigurationId>;

export const CourierOnboardingId = z.string().brand('CourierOnboardingId');
export type CourierOnboardingId = z.infer<typeof CourierOnboardingId>;

export const CourierOnboardingPdrn = z.string().brand('CourierOnboardingPdrn');
export type CourierOnboardingPdrn = z.infer<typeof CourierOnboardingPdrn>;

export const SurveyId = z.string().brand('SurveyId');
export type SurveyId = z.infer<typeof SurveyId>;

export const Timezone = z.string().brand('Timezone');
export type Timezone = z.infer<typeof Timezone>;

export const CourierDeliveryStatusType = z.enum(['pick_up', 'drop_off', 'idle', 'unknown']);

export type CourierDeliveryStatusType = z.infer<typeof CourierDeliveryStatusType>;

export const DeliveryStatusTaskStatus = z.enum(['pick_up', 'drop_off', 'idle', 'unknown']);

export type DeliveryStatusTaskStatus = z.infer<typeof DeliveryStatusTaskStatus>;

export const SchedulePlanId = z.string().brand('SchedulePlanId');
export type SchedulePlanId = z.infer<typeof SchedulePlanId>;

export const ScheduleTemplateId = z.string().brand('ScheduleTemplateId');
export type ScheduleTemplateId = z.infer<typeof ScheduleTemplateId>;

export const ScheduleTimeslotId = z.number().brand('ScheduleTimeslotId');
export type ScheduleTimeslotId = z.infer<typeof ScheduleTimeslotId>;

// https://en.wikipedia.org/wiki/ISO_4217
// https://github.com/creditornot/fleet-management/blob/2d041af11c38a4187af46124ce23d4ba56143995/modules/core/src/main/scala/com/wolt/fleetmanagement/domain/CourierCashBalance.scala#L12
export const CurrencyCode = z.string().brand('CurrencyCode');
export type CurrencyCode = z.infer<typeof CurrencyCode>;

export const CourierShiftId = z.string().brand('CourierShiftId');
export type CourierShiftId = z.infer<typeof CourierShiftId>;

export const UserId = z.string().brand('UserId');
export type UserId = z.infer<typeof UserId>;

export const ExternalUserId = z.string().brand('ExternalUserId');
export type ExternalUserId = z.infer<typeof ExternalUserId>;

export const StartingPointId = z.number().brand('StartingPointId');
export type StartingPointId = z.infer<typeof StartingPointId>;

export const RoleId = z.string().brand('RoleId');
export type RoleId = z.infer<typeof RoleId>;

export const ResourceId = z
  .enum(['earnings', 'role_management', 'scheduling.dashboard', 'city_health'])
  .or(z.never());
export type ResourceId = z.infer<typeof ResourceId>;

export const VehicleType = z.enum([
  'BICYCLE',
  'CAR',
  'MOTORCYCLE',
  'ECAR',
  'FLYING_DRONE',
  'UNRECOGNIZED',
  'EBICYCLE',
  'VAN',
  'EMOTORCYCLE',
  'ESCOOTER',
  'WALKER',
  'EBICYCLE_CARGO',
  'BICYCLE_CARGO',
  'SIDEWALK',
  'MONOWHEEL',
]);

export type VehicleType = z.infer<typeof VehicleType>;

export const SET_VEHICLE_TYPES = new Set<VehicleType>(VehicleType.options);

export const EmailLoginResponseSchema = z.object({
  email_hash: z.string(),
  password_exists: z.boolean(),
  user: z.boolean(),
  user_exists: z.boolean(),
});

export type EmailLoginResponse = z.infer<typeof EmailLoginResponseSchema>;

export const CompanyMetadata = z.object({
  companyId: CompanyId,
  userId: WoltUserId,
  createdAt: z.coerce.date(),
  companyName: z.string(),
  // TODO change to required after migration
  companyPdrn: CompanyPdrn.optional(),
});

export type CompanyMetadata = z.infer<typeof CompanyMetadata>;

export const CompanyPartnership = z.enum([
  'employee',
  'fleet-company',
  'contractor-individual',
  'contractor-company',
  'self-delivery',
  'cooperatives',
  'agency-leasing',
  'substitution',
  'saas',
  'drone-provider',
  'lcsp-company',
]);

export type CompanyPartnership = z.infer<typeof CompanyPartnership>;

export const Company = z.object({
  id: CompanyId,
  name: z.string(),
  partnership: CompanyPartnership,
  description: z.string().optional(),
  oib: z.string().optional(), // a special field used for Croatia
  countryCode: CountryCodeAlpha3,
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
});

export type Company = z.infer<typeof Company>;

export const StartingPoint = z.object({
  id: StartingPointId,
  name: z.string(),
});

export type StartingPoint = z.infer<typeof StartingPoint>;

export const MonetaryField = z.object({
  symbol: z.string(),
  displayString: z.string(),
  sign: z.boolean(),
  currency: z.string(),
  unitAmount: z.number(),
  decimalPlaces: z.number(),
});

export type MonetaryField = z.infer<typeof MonetaryField>;

const CourierAdditionalFields = z
  .object({
    // Wolt
    allowShiftReservation: z.object({ value: z.boolean() }),
    capabilities: z.object({ value: z.array(z.string()) }),
    contractType: z.object({ value: ContractType }),
    createdAt: z.object({ value: z.coerce.date() }),
    contractValidFrom: z.object({ value: z.coerce.date() }),
    contractValidTo: z.object({ value: z.coerce.date() }),
    maxDailyHours: z.object({ value: z.number() }),
    maxWeeklyHours: z.object({ value: z.number() }),
    woltCourierId: z.object({ value: CourierId }),
    languagesSpoken: z.object({ value: z.array(z.string()) }),
    // LCSP
    dasherId: z.object({ value: z.number() }),
    deactivationDate: z.object({ value: z.coerce.date() }),
    defaultStartingPoint: z.object({
      value: StartingPoint,
    }),
    hourlyRate: z.object({ value: MonetaryField }),
    isParcelRunner: z.object({ value: z.boolean() }),
    mileageRate: z.object({ value: MonetaryField }),
    phoneModel: z.object({ value: z.string() }),
    phoneOs: z.object({ value: z.string() }),
  })
  .partial();

export const LegacyCourier = z.object({
  allowShiftReservation: z.boolean().optional(),
  capabilities: z.array(z.string()).optional(),
  contractType: ContractType,
  contractValidFrom: z.coerce.date().optional(),
  contractValidTo: z.coerce.date().optional(),
  createdAt: z.coerce.date().optional(),
  languagesSpoken: z.array(z.string()).optional(),
  email: z.string(),
  firstName: z.string(),
  id: CourierId,
  isDisabled: z.boolean(),
  lastName: z.string(),
  maxDailyHours: z.number().optional(),
  maxWeeklyHours: z.number().optional(),
  name: z.string().optional(),
  phone: z.string().optional(),
  team: AreaId.optional(),
  updatedAt: z.coerce.date().optional(),
  vehicleType: VehicleType.optional(),
  // LCSP specific fields
  phone_type: z.string().optional(),
  mileage_rate: z.string().optional(),
  hourly_rate: z.string().optional(),
  is_parcel_runner: z.boolean().optional(),
  defaultStartingPoint: CourierAdditionalFields.shape.defaultStartingPoint,
});

export type LegacyCourier = z.infer<typeof LegacyCourier>;

export type CourierAdditionalFields = z.infer<typeof CourierAdditionalFields>;

export const Courier = z.object({
  id: UnifiedCourierId,
  preferredName: z.string(),
  email: z.string(),
  phone: z.string(),
  deliveryArea: z.string().optional(),
  vehicleType: VehicleType.optional(),
  additionalFields: CourierAdditionalFields,
  isActive: z.boolean(),
  companyId: CompanyId,
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  updatedBy: z.string().optional(),
  languagesSpoken: z.array(z.string()).optional(),
});

export type Courier = z.infer<typeof Courier>;

export const CourierV3 = z.object({
  ...Courier.shape,
  pdrn: CourierPdrn.optional(),
  companyPdrn: CompanyPdrn.optional(),
});

export type CourierV3 = z.infer<typeof CourierV3>;

export const CourierStatusInfo = z.object({
  courierId: UnifiedCourierId,
  companyId: CompanyId,
  status: CourierStatus,
  updatedAt: z.coerce.date().optional(),
});

export type CourierStatusInfo = z.infer<typeof CourierStatusInfo>;

export const CourierStatusInfoV2 = z.object({
  ...CourierStatusInfo.shape,
  companyPdrn: CompanyPdrn.optional(),
  courierPdrn: CourierPdrn.optional(),
});

export type CourierStatusInfoV2 = z.infer<typeof CourierStatusInfoV2>;

const TaskDuration = z.object({
  estimatedStart: z.coerce.date(),
  estimatedStop: z.coerce.date().optional(),
  actual: z.coerce.date().optional(),
});

export type TaskDuration = z.infer<typeof TaskDuration>;

export const CourierDeliveryStatusInfo = z.object({
  purchaseId: PurchaseId.optional(),
  statusType: CourierDeliveryStatusType,
  pickUpTime: TaskDuration.optional(),
  dropOffTime: TaskDuration.optional(),
});

export type CourierDeliveryStatusInfo = z.infer<typeof CourierDeliveryStatusInfo>;

export const DeliveryStatusTask = z.object({
  purchaseId: PurchaseId,
  status: DeliveryStatusTaskStatus,
  pickUpTime: z.coerce.date().optional(),
  dropOffTime: z.coerce.date().optional(),
  updatedAt: z.coerce.date(),
});

export type DeliveryStatusTask = z.infer<typeof DeliveryStatusTask>;

export const CourierDeliveryStatus = z.object({
  courierId: CourierId,
  status: CourierDeliveryStatusType,
  tasks: z.array(DeliveryStatusTask).optional(),
  updatedAt: z.coerce.date(),
});

export type CourierDeliveryStatus = z.infer<typeof CourierDeliveryStatus>;

export const CourierLocation = z.object({
  courierId: CourierId,
  companyId: CompanyId,
  latitude: z.number(),
  longitude: z.number(),
  updatedAt: z.coerce.date().optional(),
});

export type CourierLocation = z.infer<typeof CourierLocation>;

export const CourierLocationHistory = z.object({
  courierId: CourierId,
  latitude: z.number(),
  longitude: z.number(),
  speed: z.number().optional(),
  updatedAt: z.coerce.date().optional(),
});

export type CourierLocationHistory = z.infer<typeof CourierLocationHistory>;

export const CompanyContractType = z.object({
  companyId: CompanyId,
  contractType: ContractType,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type CompanyContractType = z.infer<typeof CompanyContractType>;

export const EmploymentContractType = z.enum(['Normal', 'RPC', 'MiniPilot', 'NPM', 'Pilot'], {
  message: 'This is required',
});
export type EmploymentContractType = z.infer<typeof EmploymentContractType>;

export const ContractFields = z.object({
  dailyMaximumHours: z
    .object({
      value: z.string(),
    })
    .optional(),
  weeklyMaximumHours: z
    .object({
      value: z.string(),
    })
    .optional(),
  weeklyContractHours: z
    .object({
      value: z.string(),
    })
    .optional(),
  employmentContractType: z
    .object({
      value: z.string(),
    })
    .optional(),
  nationality: z
    .object({
      value: z.string(),
    })
    .optional(),
  beneficiaryName: z
    .object({
      value: z.string(),
    })
    .optional(),
  personalId: z
    .object({
      value: z.string(),
    })
    .optional(),
  selfie: z
    .object({
      value: z.array(z.string()),
      expiresOn: z.string().optional(),
    })
    .optional(),
  identificationDocument: z
    .object({
      value: z.array(z.string()),
      expiresOn: z.string().optional(),
    })
    .optional(),
  residencePermit: z
    .object({
      value: z.array(z.string()),
      expiresOn: z.string().optional(),
    })
    .optional(),
  foodTransportationLicense: z
    .object({
      value: z.array(z.string()),
      expiresOn: z.string().optional(),
    })
    .optional(),
  driversLicense: z
    .object({
      value: z.array(z.string()),
      expiresOn: z.string().optional(),
    })
    .optional(),
  companyValidityDate: z
    .object({
      value: z.string(),
    })
    .optional(),
  insuranceNumber: z
    .object({
      value: z.string(),
    })
    .optional(),
  healthInsuranceNumber: z
    .object({
      value: z.string(),
    })
    .optional(),
  selfEmployedLicenseNumber: z

    .object({
      value: z.string(),
    })
    .optional(),
  licenseNumber: z
    .object({
      value: z.string(),
    })
    .optional(),
  fleetEmploymentContract: z
    .object({
      value: z.array(z.string()),
      expiresOn: z.string().optional(),
    })
    .optional(),
  taxCard: z
    .object({
      value: z.array(z.string()),
      expiresOn: z.string().optional(),
    })
    .optional(),
  plateNumber: z
    .object({
      value: z.array(z.string()),
      expiresOn: z.string().optional(),
    })
    .optional(),
  parentsConsentForm: z
    .object({
      value: z.array(z.string()),
      expiresOn: z.string().optional(),
    })
    .optional(),
  passport: z
    .object({
      value: z.array(z.string()),
      expiresOn: z.string().optional(),
    })
    .optional(),
  criminalRecordCheck: z
    .object({
      value: z.array(z.string()),
      expiresOn: z.string().optional(),
    })
    .optional(),
  signeeEntityName: z
    .object({
      value: z.string(),
    })
    .optional(),
  termsOfServiceAcceptance: z
    .object({
      value: z.boolean(),
    })
    .optional(),
  socialSecurityNumber: z
    .object({
      value: z.string(),
    })
    .optional(),
  addressCity: z
    .object({
      value: z.string(),
    })
    .optional(),
  address: z
    .object({
      value: z.string(),
    })
    .optional(),
  companyRegisterNumber: z
    .object({
      value: z.string(),
    })
    .optional(),
  // LCSP specific fields, treat them as contractual fields
  dateOfBirth: z
    .object({
      value: z.string(),
    })
    .optional(),
  defaultStartingPoint: z.object({ value: z.number() }).optional(),
  existingDasherEmail: z.object({ value: z.string() }).optional(),
  hourlyRate: z.object({ value: z.string() }).optional(),
  isParcelRunner: z.object({ value: z.boolean() }).optional(),
  mileageRate: z.object({ value: z.string() }).optional(),
  zipCode: z.object({ value: z.string() }).optional(),
});

export type ContractFields = z.infer<typeof ContractFields>;

export const CourierToOnboard = z.object({
  firstname: z.string(),
  lastname: z.string(),
  email: z.string(),
  phone: z.string(),
  contractType: ContractType,
  country: CountryCodeAlpha3,
  city: z.string(),
  vehicleType: VehicleType,
  contractFields: ContractFields,
  onfleetTeam: z.string(),
  allowShiftReservation: z.boolean(),
  capabilities: z.array(z.string()),
  contractValidFrom: z.coerce.date(),
  languagesSpoken: z.array(z.string()).optional(),
});

export type CourierToOnboardPayload = Omit<
  z.infer<typeof CourierToOnboard>,
  'contractValidFrom'
> & {
  id?: CourierOnboardingId;
  pdrn?: CourierOnboardingPdrn;
  // override date with string as we need to send string to api instead of date object
  contractValidFrom: string;
};

export const SelfDeliveryCourierOnboardingItem = z.object({
  email: z.string(),
  onboarded: z.boolean(),
  errors: z.array(z.string()),
});

export const SelfDeliveryCourierOnboardingResponse = z.array(SelfDeliveryCourierOnboardingItem);
export type SelfDeliveryCourierOnboardingResponse = z.infer<
  typeof SelfDeliveryCourierOnboardingResponse
>;
export type SelfDeliveryOnboardingErrorStatus = Record<string, Array<string>>;

export const CourierCashBalance = z.object({
  courierId: CourierId,
  companyId: CompanyId,
  amount: z.number(),
  currencyCode: CurrencyCode,
  updatedAt: z.coerce.date(),
});

export type CourierCashBalance = z.infer<typeof CourierCashBalance>;

export const QualityScore = z.enum(['fantastic', 'great', 'good', 'needs_improvement', 'unknown']);
export type QualityScore = z.infer<typeof QualityScore>;

export const ReliabilityScore = z.enum([
  'meets_expectations',
  'does_not_meet_expectations',
  'unknown',
]);

export type ReliabilityScore = z.infer<typeof ReliabilityScore>;

const Score = z.object({
  // https://github.com/creditornot/delivery-os-common/blob/57154b7ee028369e801623c99d599ae234d98f94/src/main/scala/com/wolt/deliveryos/common/model/metrics/performance/PerformanceScore.scala#L6
  qualityScore: QualityScore,
  reliabilityScore: ReliabilityScore,
});

// https://github.com/creditornot/delivery-os-common/blob/57154b7ee028369e801623c99d599ae234d98f94/src/main/scala/com/wolt/deliveryos/common/model/metrics/performance/PerformanceMetric.scala#L18
const MetricType = z.enum([
  // Wolt metrics
  'tar',
  'tar_shown_tasks',
  'tar_started_tasks',
  'tcr',
  'dph',
  'num_deliveries',
  'online_hours',
  'on_task_hours',
  'idle_hours',
  'online_distance',
  'on_task_distance',
  // LCSP metrics
  'attendance_rate',
  'acceptance_rate',
  'order_efficiency',
  'missing_and_incorrect',
  'never_delivered',
  'total_lateness',
  'parcel_acceptance_rate',
  'parcel_never_delivered_rate',
  'parcel_attempted_delivery_rate',
  'parcel_proof_of_delivery_rate',
  'parcel_completion_rate',
  'utilization',
  'unknown',
]);

export type MetricType = z.infer<typeof MetricType>;

export const Metric = z.object({
  metricType: MetricType,
  value: z.number(),
  isThresholdMet: z.boolean().optional(),
  threshold: z.number().optional(),
  score: Score.optional(),
  updatedAt: z.coerce.date().optional(),
});

export type Metric = z.infer<typeof Metric>;

// https://github.com/creditornot/delivery-os-common/blob/0383403b0a8d5457dcfe6865c25a42a1fff7ea0e/src/main/scala/com/wolt/deliveryos/common/model/metrics/performance/PerformanceGraph.scala#L20
const GraphMetricType = z.enum([
  'attendance_rate',
  'attendance_rate_goal',
  'acceptance_rate',
  'acceptance_rate_goal',
  'order_efficiency',
  'order_efficiency_goal',
  'missing_and_incorrect',
  'missing_and_incorrect_goal',
  'wrong_order_delivered',
  'wrong_order_delivered_goal',
  'never_delivered',
  'never_delivered_goal',
  'total_lateness',
  'total_lateness_goal',
  'r2c_lateness_rate',
  'd2r_lateness_rate',
  'handoff_lateness_rate',
  'pickup_lateness_rate',
  'drop_off_lateness_rate',
  'utilization',
  'utilization_goal',
  'unknown',
]);

export type GraphMetricType = z.infer<typeof GraphMetricType>;

const Graph = z.object({
  metricType: GraphMetricType,
  dataPoints: z.array(
    z.object({
      weekStart: z.coerce.date(),
      weekEnd: z.coerce.date(),
      value: z.number(),
    }),
  ),
});

export type Graph = z.infer<typeof Graph>;

export const BaseAggregationType = z.enum([
  'daily',
  'weekly',
  'monthly',
  'lifetime',
  'l100deliveries',
  'l400deliveries',
  'time_period',
  'unknown',
]);

export type BaseAggregationType = z.infer<typeof BaseAggregationType>;

export const LcspAggregationType = z.enum([
  'weekly',
  'l100deliveries',
  'l400deliveries',
  'unknown',
]);

export type LcspAggregationType = z.infer<typeof LcspAggregationType>;

export const AggregationType = z.enum([
  ...BaseAggregationType.options,
  ...LcspAggregationType.options,
]);

export type AggregationType = z.infer<typeof AggregationType>;

export const CourierMetrics = z.object({
  courierId: UnifiedCourierId,
  aggregationType: BaseAggregationType,
  metrics: z.array(Metric),
  graphData: z.array(Graph),
  score: Score.optional(),
  percentileRank: z.number().optional(),
});

export type CourierMetrics = z.infer<typeof CourierMetrics>;

export const CourierMetricsV1MetricType = z.enum([
  'acceptance_rate',
  'attendance_rate',
  'utilization',
  'idle_hours',
  'total_idle_time',
  'distinct_active_time',
  'online_hours',
  'on_task_hours',
  'never_delivered',
  'missing_and_incorrect',
  'total_lateness',
  'percentile',
  'num_deliveries',
  'dph',
  'online_distance',
  'on_task_distance',
  'pause_time',
  'tar_shown_tasks',
  'tar_started_tasks',
  'tcr',
  'unknown',
]);

export type CourierMetricsV1MetricType = z.infer<typeof CourierMetricsV1MetricType>;

export const CourierMetricsV1GraphMetricType = z.enum([
  // LCSP trends
  'attendance_rate',
  'attendance_rate_goal',
  'acceptance_rate',
  'acceptance_rate_goal',
  'order_efficiency',
  'order_efficiency_goal',
  'missing_and_incorrect',
  'missing_and_incorrect_goal',
  'wrong_order_delivered',
  'wrong_order_delivered_goal',
  'never_delivered',
  'never_delivered_goal',
  'total_lateness',
  'total_lateness_goal',
  'r2c_lateness_rate',
  'd2r_lateness_rate',
  'handoff_lateness_rate',
  'pickup_lateness_rate',
  'drop_off_lateness_rate',
  'utilization',
  'utilization_goal',
  // Wolt trends
  'tar',
  'tar_goal',
  'dph',
  'dph_goal',
  'tcr',
  'tcr_goal',
  // Common
  'unknown',
]);

export type CourierMetricsV1GraphMetricType = z.infer<typeof CourierMetricsV1GraphMetricType>;

export const CourierMetricsV1Dimension = z.enum(['courier', 'city', 'deliveryArea']);

export type CourierMetricsV1Dimension = z.infer<typeof CourierMetricsV1Dimension>;

const CourierMetricsV1DataPoint = z.object({
  periodStart: z.string(),
  periodEnd: z.string(),
  value: z.number(),
});

export const CourierMetricsV1Metric = z.object({
  metricType: CourierMetricsV1MetricType,
  value: z.number(),
  threshold: z.number().optional(),
  isThresholdMet: z.boolean().optional(),
  score: Score.optional(),
  updatedAt: z.string().optional(),
});

export type CourierMetricsV1Metric = z.infer<typeof CourierMetricsV1Metric>;

const CourierMetricsV1GraphSeries = z.object({
  metricType: CourierMetricsV1GraphMetricType,
  dataPoints: z.array(CourierMetricsV1DataPoint),
});

export const CourierMetricsV1EntityReference = z.discriminatedUnion('dimension', [
  z.object({
    dimension: z.literal('courier'),
    id: UnifiedCourierId,
    pdrn: CourierPdrn,
    name: z.string().optional(),
  }),
  z.object({
    dimension: z.literal('city'),
    id: z.string(),
    pdrn: z.string(),
    name: z.string().optional(),
  }),
  z.object({
    dimension: z.literal('deliveryArea'),
    id: z.string(),
    pdrn: z.string(),
    name: z.string().optional(),
  }),
]);

export type CourierMetricsV1EntityReference = z.infer<typeof CourierMetricsV1EntityReference>;

export const CourierMetricsV1Entry = z.object({
  entity: CourierMetricsV1EntityReference,
  score: Score.optional(),
  percentileRank: z.number().optional(),
  metrics: z.array(CourierMetricsV1Metric),
  graphData: z.array(CourierMetricsV1GraphSeries).optional(),
});

export type CourierMetricsV1Entry = z.infer<typeof CourierMetricsV1Entry>;

export const CourierMetricsV1Response = z.object({
  meta: z.object({
    aggregationType: BaseAggregationType,
    timeframe: z.object({
      from: z.number(),
      to: z.number(),
    }),
    dimension: CourierMetricsV1Dimension,
  }),
  data: z.array(CourierMetricsV1Entry),
});

export type CourierMetricsV1Response = z.infer<typeof CourierMetricsV1Response>;

export const CityConfig = z.object({
  id: CityConfigId,
  slug: z.string(),
  countryCode: CountryCodeAlpha3,
  availableVehicleTypes: z.array(VehicleType),
});

export type CityConfig = z.infer<typeof CityConfig>;

export const Area = z.object({
  name: AreaId,
  description: z.string().optional(),
  city: z.string(),
  cityName: z.string().optional(),
  printedName: z.string(),
  groupingName: z.string(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
});

export type Area = z.infer<typeof Area>;

export const WolfUploadResponse = z.object({
  id: z.string(),
  createdAt: z.coerce.date(),
});

export type WolfUploadResponse = z.infer<typeof WolfUploadResponse>;

export const EncodedFileId = z.string();
export type EncodedFileId = z.infer<typeof EncodedFileId>;

export const BlobSchema = z.instanceof(Blob);
export type Blob = z.infer<typeof BlobSchema>;

export const CourierGroup = z.object({
  id: CourierGroupId,
  companyId: CompanyId,
  name: z.string(),
  description: z.string().optional(),
  courierIds: z.array(UnifiedCourierId).optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
});

export type CourierGroup = z.infer<typeof CourierGroup>;

export const CourierGroupV2 = z.object({
  ...CourierGroup.shape,
  pdrn: CourierGroupPdrn.optional(),
  companyPdrn: CompanyPdrn.optional(),
  courierPdrns: z.array(CourierPdrn).optional(),
});

export type CourierGroupV2 = z.infer<typeof CourierGroupV2>;

export const TransactionType = z.enum([
  'transaction_type_task_base_cost',
  'transaction_type_task_distance_cost',
  'transaction_type_bonus_cost',
  'transaction_type_task_capability_cost',
  'transaction_type_manual',
  'transaction_type_upfront_pricing_adjustment',
  'transaction_type_shift_guarantee',
  'transaction_type_correction',
  'transaction_type_payout',
  'transaction_type_tip',
  'transaction_type_task_pickup_distance_cost',
  'transaction_type_task_pickup_base_cost',
  'transaction_type_dph_bonus_cost',
  'transaction_type_task_earnings_floor_cost',
  'transaction_type_effort_adjustment_cost',
  'transaction_type_acceptance_cost',
  'transaction_type_activation_cost',
  'transaction_type_post_delivery_adjustment',
  'transaction_type_unspecified',
  'transaction_type_pickup_estimated_execution_time_cost',
  'transaction_type_dropoff_estimated_execution_time_cost',
  'transaction_type_pickup_wait_time_cost',
  'transaction_type_new_lead_bonus_cost',
  'transaction_type_referrer_bonus_cost',
  'transaction_type_micro_task_cost',
  'unknown',
]);

export type TransactionType = z.infer<typeof TransactionType>;

export const CourierTransaction = z.object({
  transactionType: TransactionType,
  currency: CurrencyCode,
  amount: z.number(),
});
export type CourierTransaction = z.infer<typeof CourierTransaction>;

export const CourierEarnings = z.object({
  courierId: CourierId,
  companyId: CompanyId,
  aggregatedTransactions: z.array(CourierTransaction),
});

export type CourierEarnings = z.infer<typeof CourierEarnings>;

export const CourierTravelledDistance = z.object({
  courierId: CourierId,
  onTaskDistanceMetres: z.number(),
  travelledDistanceMetres: z.number(),
  updatedAt: z.coerce.date().optional(),
});

export type CourierTravelledDistance = z.infer<typeof CourierTravelledDistance>;

export const CourierTravelledDistanceV2 = z.object({
  courierId: CourierId,
  onlineDistance: z.object({
    travelledDistanceMetres: z.number(),
    updatedAt: z.coerce.date().optional(),
  }),
  onTaskDistance: z.object({
    travelledDistanceMetres: z.number(),
    updatedAt: z.coerce.date().optional(),
  }),
});

export type CourierTravelledDistanceV2 = z.infer<typeof CourierTravelledDistanceV2>;

export const CourierDeliveriesStatus = z.enum([
  'assigned',
  'cancelled',
  'committed',
  'completed',
  'drop_off',
  'pick_up',
  'unassigned',
  'unknown',
]);

export type CourierDeliveriesStatus = z.infer<typeof CourierDeliveriesStatus>;

export const CourierDeliveries = z.object({
  id: z.number(), // task group ID
  courierId: CourierId,
  purchaseId: PurchaseId,
  companyId: CompanyId,
  status: CourierDeliveriesStatus,
  pickUpTime: z.coerce.date().optional(),
  dropOffUpTime: z.coerce.date().optional(),
  updatedAt: z.coerce.date(),
});

export type CourierDeliveries = z.infer<typeof CourierDeliveries>;

const ExperimentNames = z.string();

export const Experiments = z.object({
  experiments: z.record(ExperimentNames, z.string()).optional(),
});

export type Experiments = z.infer<typeof Experiments>;

export const FeatureFlags = z.object({});

export type FeatureFlags = z.infer<typeof FeatureFlags>;

const ConfigurationBase = z.object({
  configValues: z.unknown(),
  createdAt: z.coerce.date(),
  description: z.string().optional(),
  id: ConfigurationId,
  identifier: z.string().optional(),
  lastUpdatedBy: WoltUserId,
  level: z.enum(['company', 'country', 'default']),
  name: z.string(),
  slug: z.unknown(),
  updatedAt: z.coerce.date(),
});

export const ConfigurationAdvancedFiltersInAllCouriers = ConfigurationBase.extend({
  configValues: z.object({
    threshold_cash_carry: z.object({
      value: z.number(),
    }),
  }),
  slug: z.literal('advanced_filters_in_all_couriers'),
});

export type ConfigurationAdvancedFiltersInAllCouriers = z.infer<
  typeof ConfigurationAdvancedFiltersInAllCouriers
>;

export const ConfigurationAdvancedFiltersInPerformance = ConfigurationBase.extend({
  configValues: z.object({
    threshold_task_acceptance_rate: z.object({
      value: z.number(),
    }),
  }),
  slug: z.literal('advanced_filters_in_performance'),
});

export type ConfigurationAdvancedFiltersInPerformance = z.infer<
  typeof ConfigurationAdvancedFiltersInPerformance
>;

const DefaultFieldConfig = z.object({
  allowEdit: z.boolean(),
});

export const ConfigurationForCourierInformation = z.object({
  slug: z.literal('delivery-os-courier-information'),
  configValues: z.object({
    name: z.object({
      value: DefaultFieldConfig,
    }),
    team: z.object({
      value: DefaultFieldConfig,
    }),
    allowShiftReservation: z.object({
      value: DefaultFieldConfig,
    }),
    vehicleType: z.object({
      value: DefaultFieldConfig,
    }),
    email: z
      .object({
        value: DefaultFieldConfig,
      })
      .optional(),
    phoneNumber: z
      .object({
        value: DefaultFieldConfig,
      })
      .optional(),
    contractType: z
      .object({
        value: DefaultFieldConfig,
      })
      .optional(),
    capabilities: z
      .object({
        value: DefaultFieldConfig,
      })
      .optional(),
    contractValidFrom: z
      .object({
        value: DefaultFieldConfig,
      })
      .optional(),
    contractValidTo: z
      .object({
        value: DefaultFieldConfig,
      })
      .optional(),
    maxDailyHours: z
      .object({
        value: DefaultFieldConfig,
      })
      .optional(),
    maxWeeklyHours: z
      .object({
        value: DefaultFieldConfig,
      })
      .optional(),
  }),
});

export const ConfigurationForScheduling = z.object({
  slug: z.literal('delivery-os-scheduling'),
  configValues: z.object({
    enable_scheduling: z.object({
      value: z.boolean(),
    }),
  }),
});

export type ConfigurationForScheduling = z.infer<typeof ConfigurationForScheduling>;

export type ConfigurationForCourierInformation = z.infer<typeof ConfigurationForCourierInformation>;

export const ConfigurationScorecardThresholds = ConfigurationBase.extend({
  slug: z.literal('scorecard_thresholds_in_overview'),
  configValues: z.object({
    scorecard_task_acceptance_rate: z
      .object({
        value: z.object({
          base: z.array(z.number()).optional(),
          cities: z.record(z.string(), z.array(z.number())).optional(),
        }),
      })
      .optional(),
    scorecard_distinct_active_time: z
      .object({
        value: z.object({
          base: z.array(z.number()).optional(),
          cities: z.record(z.string(), z.array(z.number())).optional(),
        }),
      })
      .optional(),
  }),
});

export type ConfigurationScorecardThresholds = z.infer<typeof ConfigurationScorecardThresholds>;

export const ConfigurationCompanySettings = ConfigurationBase.extend({
  slug: z.literal('delivery-os-company-settings'),
  configValues: z.object({
    rewards_program_enabled: z
      .object({
        value: z.boolean(),
      })
      .optional(),
    hot_spots_enabled: z
      .object({
        value: z.boolean(),
      })
      .optional(),
    allow_to_enable_couriers: z
      .object({
        value: z.boolean(),
      })
      .optional(),
    rejection_strikes_enabled: z
      .object({
        value: z.boolean(),
      })
      .optional(),
  }),
});

const FeatureEnabled = z.discriminatedUnion('id', [
  z.object({ id: z.literal('enable_routing_to_the_service') }),
  z.object({ id: z.literal('enable_performance_metrics_v4') }),
  z.object({ id: z.literal('enable_lcsp_courier_shift_status') }),
  z.object({ id: z.literal('enable_pedregal_courier_listing') }),
]);

export const ConfigurationDeliveryOsGateway = ConfigurationBase.extend({
  slug: z.literal('delivery-os-gateway'),
  configValues: z.object({
    features_enabled: z
      .object({
        value: z
          .preprocess(
            raw =>
              Array.isArray(raw)
                ? raw.filter(value => FeatureEnabled.safeParse(value).success)
                : raw,
            z.array(FeatureEnabled),
          )
          .optional(),
      })
      .optional(),
  }),
});

export const ConfigurationDeliveryOsPedregal = ConfigurationBase.extend({
  slug: z.literal('delivery-os-pedregal-rollout'),
  configValues: z.object({
    pedregal_onboarding_enabled: z.object({ value: z.boolean() }).optional(),
  }),
});
export type ConfigurationDeliveryOsPedregal = z.infer<typeof ConfigurationDeliveryOsPedregal>;

export type ConfigurationCompanySettings = z.infer<typeof ConfigurationCompanySettings>;

export const CompanySettings = z.object({
  enableRejectionManagement: z.boolean().optional(),
});

export type CompanySettings = z.infer<typeof CompanySettings>;

export const CourierOnboardingStatus = z.enum([
  'draft',
  'error',
  'in_progress',
  'rejected',
  'submitted',
  'success',
]);

export const LastCourierOnboardingResultStatus = z.enum([
  'success',
  'invalid_data',
  'invalid_contract_data',
  'internal_error',
  'moved_to_reapply',
  'moved_to_reapply_error',
  'terms_agreement_not_created',
  'contract_already_approved',
  'contract_template_not_found',
  'phone_mismatch',
  'phone_duplicate',
  'unable_to_get_courier_for_user',
  'wolt_auth_error',
  'hellosign_not_supported',
]);

export type LastCourierOnboardingResultStatus = z.infer<typeof LastCourierOnboardingResultStatus>;

export type CourierOnboardingStatus = z.infer<typeof CourierOnboardingStatus>;

export const CourierOnboarding = z.object({
  id: CourierOnboardingId.optional(),
  pdrn: CourierOnboardingPdrn.optional(),
  firstname: z.string().optional(),
  lastname: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  country: CountryCodeAlpha3,
  city: z.string().optional(),
  // CET-1582: Normalize empty string to undefined before validation.
  //
  // The backend allows empty strings for vehicleType via API, but the frontend
  // expects either a valid VehicleType enum value or null/undefined. When an
  // empty string is returned, Zod validation fails and the entire onboarding
  // list fails to parse, causing the page to display "no data".
  //
  // This preprocessing converts '' → undefined at the parsing boundary,
  // treating empty strings as "not provided" rather than invalid data.
  //
  // Context: https://doordash.atlassian.net/browse/CET-1582
  vehicleType: z.preprocess(val => (val === '' ? undefined : val), VehicleType.optional()),
  contractType: ContractType,
  contractValidFrom: z.coerce.date().optional(),
  contractFields: ContractFields.optional(),
  onfleetTeam: z.string().optional(),
  allowShiftReservation: z.boolean().optional(),
  capabilities: z.array(z.string()).optional(),
  status: CourierOnboardingStatus,
  errors: z.array(z.string()).optional(),
  comment: z.string().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  lastUpdatedBy: WoltUserId,
  lastOnboardingResultStatus: LastCourierOnboardingResultStatus.optional(),
  languagesSpoken: z.array(z.string()).optional(),
  referredBy: z.string().optional(),
});

export type CourierOnboarding = z.infer<typeof CourierOnboarding>;

export const CourierOnboardingPayload = CourierOnboarding.extend({
  contractFields: ContractFields,
  status: CourierOnboardingStatus.optional(),
  contractValidFrom: z.string().optional(),
}).pick({
  firstname: true,
  lastname: true,
  email: true,
  phone: true,
  country: true,
  city: true,
  vehicleType: true,
  contractType: true,
  contractValidFrom: true,
  contractFields: true,
  onfleetTeam: true,
  allowShiftReservation: true,
  capabilities: true,
  status: true,
});

export type CourierOnboardingPayload = z.infer<typeof CourierOnboardingPayload>;

const SurveyBase = z.object({
  id: SurveyId,
  slug: z.unknown(),
  userId: WoltUserId,
  createdAt: z.coerce.date(),
});

export const DeliveryOsSurvey = SurveyBase.extend({
  slug: z.literal('delivery-os'),
  email: z.string().optional(),
  phone: z.string().optional(),
  name: z.string().optional(),
  language: z.string().optional(),
  rating: z.coerce.number(),
  feedback: z.string().optional(),
});

export type DeliveryOsSurvey = z.infer<typeof DeliveryOsSurvey>;
export const EdgeCampingIncidentStatus = z.enum(['open', 'closed', 'unknown']);
export type EdgeCampingIncidentStatus = z.infer<typeof EdgeCampingIncidentStatus>;

export const EdgeCampingIncident = z.object({
  courierId: CourierId,
  incidentOpenedAt: z.coerce.date(),
  incidentClosedAt: z.coerce.date().optional(),
  reason: z.string().optional(),
  companyId: CompanyId,
  status: EdgeCampingIncidentStatus,
  eventTime: z.coerce.date(),
});

export type EdgeCampingIncident = z.infer<typeof EdgeCampingIncident>;

export const CapabilitiesSettings = z.object({
  id: CompanyId,
  whiteList: z.array(z.string()),
  allowAll: z.boolean(),
  updatedAt: z.coerce.date(),
  createdAt: z.coerce.date(),
});

export type CapabilitiesSettings = z.infer<typeof CapabilitiesSettings>;

export const CourierCompany = z.object({
  companyId: CompanyId,
});

export type CourierCompany = z.infer<typeof CourierCompany>;

export const Location = z.object({
  longitude: z.number(),
  latitude: z.number(),
});
export type Location = z.infer<typeof Location>;

export const Venue = z.object({
  id: VenueId,
  countryCode: CountryCodeAlpha3,
  name: z.string(),
  location: Location,
});
export type Venue = z.infer<typeof Venue>;

export const ExternalTaskCreationRequest = z.object({
  venue_id: VenueId,
  order_reference: z.string(),
  drop_off: z.object({
    location: z.object({
      coordinates: z.object({
        latitude: z.number(),
        longitude: z.number(),
      }),
      address: z.object({
        country: CountryCodeAlpha3,
        city: z.string(),
        street_address: z.string(),
        zip_code: z.string(),
      }),
    }),
    comment: z.string(),
  }),
  courier_assignment: z.object({
    courier_id: z.string(),
  }),
  recipient: z.object({
    first_name: z.string(),
    last_name: z.string(),
    phone_number: z.string(),
  }),
});

export type ExternalTaskCreationRequest = z.infer<typeof ExternalTaskCreationRequest>;

export const ExternalTaskCreationResponse = z.object({
  id: ExternalTaskId,
  order_number: z.string(),
  recipient: z.object({
    first_name: z.string(),
    last_name: z.string(),
    phone_number: z.string(),
  }),
  courier_assignment: z.object({
    internal_courier_id: z.string(),
  }),
});

export type ExternalTaskCreationResponse = z.infer<typeof ExternalTaskCreationResponse>;

export const AddressAutocompleteRequest = z.object({
  company: CompanyId,
  input: z.string(),
  components: z.string().optional(),
});

export type AddressAutocompleteRequest = z.infer<typeof AddressAutocompleteRequest>;

export const AddressAutocompleteResponse = z.object({
  predictions: z.array(
    z.object({
      place_id: z.string(),
      description: z.string(),
      structured_formatting: z.object({
        main_text: z.string(),
        secondary_text: z.string(),
      }),
    }),
  ),
});

export type AddressAutocompleteResponse = z.infer<typeof AddressAutocompleteResponse>;

export const GetAddressByPlaceIdRequest = z.object({
  company: CompanyId,
  placeId: z.string(),
});

export type GetAddressByPlaceIdRequest = z.infer<typeof GetAddressByPlaceIdRequest>;

export const GetAddressByPlaceIdResponse = z.object({
  coordinates: z.object({
    latitude: z.number(),
    longitude: z.number(),
  }),
  street: z.string(),
  post_code: z.string(),
  city: z.string(),
  country: CountryCodeAlpha3,
});

export type GetAddressByPlaceIdResponse = z.infer<typeof GetAddressByPlaceIdResponse>;

export const TimeslotTime = z.string().datetime({ offset: true });

export const ScheduleTimeslot = z.object({
  id: ScheduleTimeslotId,
  planId: SchedulePlanId,
  startTime: TimeslotTime,
  endTime: TimeslotTime,
  localDate: z.string().date(),
  sequenceId: z.number(),
  capacity: z.number(),
  createdAt: z.coerce.date(),
  assignedSlots: z.number(),
  clockedSlots: z.number(),
  unassignedOnlineSlots: z.number(),
  hardCapped: z.boolean(),
});

export type ScheduleTimeslot = z.infer<typeof ScheduleTimeslot>;

export const SchedulePlan = z.object({
  id: SchedulePlanId,
  companyId: CompanyId,
  name: z.string(),
  isActive: z.boolean(),
  startDate: z.coerce.date(),
  endDate: z.coerce.date(),
  createdAt: z.coerce.date(),
  slotDurationMinutes: z.number(),
  area: AreaId,
  vehicleType: z.array(VehicleType),
  timezone: Timezone,
  isPreview: z.boolean().optional(),
});

export type SchedulePlan = z.infer<typeof SchedulePlan>;

export const PublishScheduleResponsePayload = z.object({
  name: z.string(),
  isActive: z.boolean(),
});

export type PublishScheduleResponsePayload = z.infer<typeof PublishScheduleResponsePayload>;

export const CourierScheduleStatistics = z.object({
  courierId: z.string(),
  totalAssignedSlots: z.number(),
  pastAssignedSlots: z.number(),
  workedSlots: z.number(),
});

export type CourierScheduleStatistics = z.infer<typeof CourierScheduleStatistics>;

const Shift = z.object({
  id: CourierShiftId,
  courierId: z.string(),
  clockInTime: z.string().datetime(),
  clockOutTime: z.string().datetime().optional(),
});

export const CourierShifts = z.object({
  shifts: z.array(Shift).optional(),
  fulfilledTimeslots: z.array(ScheduleTimeslot).optional(),
  unfulfilledTimeslots: z.array(ScheduleTimeslot).optional(),
});

export type CourierShifts = z.infer<typeof CourierShifts>;

export const User = z.object({
  id: UserId,
  email: z.string().email(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  roles: z.array(z.string()).optional(),
});

export const ErrorCourierOnboardingResponse = z.object({
  code: z.number(),
  message: z.string(),
  details: z.string().optional(),
});

export type ErrorCourierOnboardingResponse = z.infer<typeof ErrorCourierOnboardingResponse>;

export const UserRole = z.object({
  id: RoleId,
  name: z.string(),
});

export type UserRole = z.infer<typeof UserRole>;

export const Permission = z.preprocess(
  val => (Array.isArray(val) ? new Set(val) : val),
  z.set(z.enum(['c', 'r', 'u', 'd'])),
);

export type Permission = z.infer<typeof Permission>;

export const ResourcePermissions = z.object({
  resourceId: ResourceId,
  permissions: Permission,
});

export type ResourcePermissions = z.infer<typeof ResourcePermissions>;

export const UserPermissions = z.object({
  role: UserRole,
  resourcePermissions:
    // We don't want to stop the parsing if there are any unused resource IDs in
    // the data. Therefore, any unused resource IDs will be excluded during preprocessing.
    // Unused resource IDs: Any resource IDs not listed in the ResourceId enum.
    z
      .preprocess(
        rawValue =>
          Array.isArray(rawValue)
            ? rawValue.filter(value => ResourceId.safeParse(value.resourceId).success)
            : rawValue,
        z.array(ResourcePermissions),
      )
      .optional(),
});

export type UserPermissions = z.infer<typeof UserPermissions>;

export const Permissions = z.partialRecord(ResourceId, Permission);

export type Permissions = z.infer<typeof Permissions>;

export const CourierWorkedHours = z.object({
  courierId: UnifiedCourierId,
  workedMinutes: z.number(),
  isAboveThreshold: z.boolean(),
});

export type CourierWorkedHours = z.infer<typeof CourierWorkedHours>;

export const DetailedTimeslot = z.object({
  timeslot: ScheduleTimeslot.omit({
    unassignedOnlineSlots: true,
    assignedSlots: true,
    clockedSlots: true,
  }),
  assignedCouriers: z.array(UnifiedCourierId).optional(),
  workedCouriers: z.array(CourierWorkedHours).optional(),
  onlineCouriers: z.array(UnifiedCourierId).optional(),
  offlineCouriers: z.array(UnifiedCourierId).optional(),
  notWorkedCouriers: z.array(UnifiedCourierId).optional(),
  unassignedOnlineCouriers: z.array(UnifiedCourierId).optional(),
  unassignedButWorkedCouriers: z.array(CourierWorkedHours).optional(),
});

export type DetailedTimeslot = z.infer<typeof DetailedTimeslot>;

const ScheduleType = z.enum(['TIMESLOT_BASED', 'SHIFT_BASED']);

const ScheduleInfoDateRange = z.object({
  startDate: z.iso.datetime(),
  endDate: z.iso.datetime(),
});

export type ScheduleInfoDateRange = z.infer<typeof ScheduleInfoDateRange>;

export const ScheduleInfo = z.object({
  companyId: CompanyId,
  dateRange: ScheduleInfoDateRange,
  timezone: Timezone,
  scheduleType: ScheduleType,
});

export type ScheduleInfo = z.infer<typeof ScheduleInfo>;

export const ScheduleShiftId = z.number().brand('ScheduleShiftId');
export type ScheduleShiftId = z.infer<typeof ScheduleShiftId>;

const ScheduleRangeMetadata = z.object({
  dos: z
    .object({
      timeslotId: ScheduleTimeslotId,
      status: z.enum(['ASSIGNED', 'COMPLETED', 'NO_SHOW']),
    })
    .optional(),
  lcsp: z
    .object({
      shiftId: ScheduleShiftId,
      startingPointId: StartingPointId.optional(),
      startTime: z.iso.datetime().optional(),
      endTime: z.iso.datetime().optional(),
    })
    .optional(),
});

const ScheduleRange = z.object({
  startTime: z.iso.datetime(),
  endTime: z.iso.datetime(),
  metadata: ScheduleRangeMetadata,
});

export type ScheduleRange = z.infer<typeof ScheduleRange>;

const DailySchedule = z.object({
  localDate: z.iso.date(),
  scheduledRanges: z.array(ScheduleRange).optional(),
});

const CourierSchedule = z.object({
  courierId: UnifiedCourierId,
  dailySchedules: z.array(DailySchedule).optional(),
});

export type CourierSchedule = z.infer<typeof CourierSchedule>;

export type DailySchedule = z.infer<typeof DailySchedule>;

export const UnifiedSchedule = z.object({
  scheduleInfo: ScheduleInfo,
  timeslots: z.array(DetailedTimeslot).optional(),
  schedulePlan: SchedulePlan.optional(),
  courierSchedules: z.array(CourierSchedule).optional(),
});

export type UnifiedSchedule = z.infer<typeof UnifiedSchedule>;

export const UnifiedAssignmentActions = z.enum(['ASSIGN', 'UNASSIGN', 'UPDATE']);
export type UnifiedAssignmentActions = z.infer<typeof UnifiedAssignmentActions>;

export const UnifiedAssignmentTimeRange = z.object({
  startTime: z.iso.datetime(),
  endTime: z.iso.datetime(),
});

export type UnifiedAssignmentTimeRange = z.infer<typeof UnifiedAssignmentTimeRange>;

export const UnifiedAssignment = z.object({
  courierId: UnifiedCourierId,
  timeslotId: ScheduleTimeslotId.optional(),
  shiftId: ScheduleShiftId.optional(),
  startTime: z.iso.datetime(),
  endTime: z.iso.datetime().optional(),
  error: z
    .object({
      message: z.string(),
      code: z.string(),
    })
    .optional(),
});

export type UnifiedAssignment = z.infer<typeof UnifiedAssignment>;

export const CompanyExternalUser = z.object({
  userId: UserId,
  roleId: RoleId,
  createdAt: z.coerce.date(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().optional(),
  updatedAt: z.coerce.date().optional(),
});

export type CompanyExternalUserT = z.infer<typeof CompanyExternalUser>;

export const CityCoordinate = z.object({
  latitude: z.number(),
  longitude: z.number(),
});

export const ScheduleDashboard = z.object({
  totalCapacity: z.number(),
  totalOnlineCouriers: z.number(),
  city: z.string(),
  coordinates: z.preprocess(val => (val === null ? undefined : val), CityCoordinate.optional()),
});

export type CityCoordinate = z.infer<typeof CityCoordinate>;
export type ScheduleDashboard = z.infer<typeof ScheduleDashboard>;

// https://woltwide.atlassian.net/wiki/spaces/SD/pages/4608032836/City+Health+-+Feature+Overview
export const CityHealthStatus = z.enum([
  'grey',
  'blue',
  'green',
  'yellow',
  'orange',
  'red',
  'unknown',
]);

export const CityHealthItem = z.object({
  city: z.string(),
  status: CityHealthStatus,
});

export const CityHealthResponse = z.object({
  results: z.array(CityHealthItem),
});

export type CityHealthStatus = z.infer<typeof CityHealthStatus>;
export type CityHealthItem = z.infer<typeof CityHealthItem>;
export type CityHealthResponse = z.infer<typeof CityHealthResponse>;
